/** UI filter key → API `type` query value */
const INVOICE_API_TYPE_BY_FILTER = {
  submission: "submission",
  negative_credit: "negative_credit",
  subscription: "subscription",
  topup: "top_up",
  postpay: "postpay",
};

export const INVOICE_TYPE_LABELS = {
  submission: "Submission",
  negative_credit: "Adjustment",
  subscription: "Subscription",
  topup: "Top-up",
  postpay: "Postpay",
  rampup: "Ramp-up",
  implementation: "Implementation",
};

/** API `type` values that share a UI key */
const INVOICE_TYPE_ALIASES = {
  top_up: "topup",
  ramp_up: "rampup",
  "ramp-up": "rampup",
};

/**
 * Normalize API invoice type to UI key.
 * @param {string} type
 * @returns {'submission'|'negative_credit'|'subscription'|'topup'|'postpay'|'rampup'|'implementation'|string}
 */
const normalizeInvoiceType = (type) => {
  const key = String(type || "").trim().toLowerCase();
  return INVOICE_TYPE_ALIASES[key] || key;
};

/**
 * Checkout `purpose` for Pay Now, by invoice type.
 * Top-up is a credit recharge. Adjustment is a negative credit.
 * Subscription, Rampup, Implementation, and any other type use invoice.
 * @param {string} type
 * @returns {'recharge'|'negative_credit'|'invoice'}
 */
export const resolveInvoicePaymentPurpose = (type) => {
  const key = normalizeInvoiceType(type);
  if (key === "topup") return "recharge";
  if (key === "negative_credit") return "negative_credit";
  return "invoice";
};

/**
 * Resolve API `type` param from active UI type filters.
 * One value, or several joined with commas and no spaces.
 * Omit it when none are selected so the API returns all types.
 * @param {string[]} activeTypes
 * @returns {string|undefined}
 */
export const resolveInvoiceTypeParam = (activeTypes = []) => {
  const types = activeTypes
    .map((key) => INVOICE_API_TYPE_BY_FILTER[key])
    .filter(Boolean);
  if (!types.length) return undefined;
  return types.join(",");
};

/**
 * Map API invoice records into table row shape.
 * @param {Array} invoices
 * @returns {Array}
 */
export const mapInvoicesToRows = (invoices) => (
  invoices.map((invoice) => ({
    id: invoice.invoice_number,
    invoiceId: invoice.invoice_id,
    name: invoice.invoice_name,
    date: invoice.invoice_date,
    type: normalizeInvoiceType(invoice.type),
    createdBy: String(invoice.created_by || "").trim(),
    billTo: String(invoice.bill_to || "").trim(),
    amount: invoice.amount,
    currency: invoice.currency,
    paymentStatus: invoice.status,
    pdfPath: invoice.pdf_path,
  }))
);

/**
 * Format invoice amount for display.
 * A missing or non-numeric amount is left blank.
 * @param {string|number} amount
 * @param {string} currency
 * @returns {string}
 */
export const formatInvoiceAmount = (amount, currency) => {
  if (amount == null || amount === "") return "";

  const value = Number(amount);
  if (!Number.isFinite(value)) return "";

  try {
    return value.toLocaleString("en-US", { style: "currency", currency });
  } catch (error) {
    // Missing or invalid currency: show the amount without a symbol
    return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
};
